import { useEffect, useMemo, useReducer, useRef, useState } from "react";
import { Card, PageError, PageLoading } from "../index.js";
import { useApiAction } from "../../hooks/useApi.js";
import { useAuth } from "../../context/AuthContext.jsx";
import { ProductOrderForm } from "./ProductOrderForm.jsx";
import { ProductOrderActionModal } from "./ProductOrderActionModal.jsx";
import { StatusChip } from "./ProductOrderList.jsx";
import {
  DETAIL_ACTION_LABELS as ACTION_LABELS, PRODUCT_TYPE_LABELS, availableActions, billingActions, billingDescription, canActOnDetail, copyText, createDetailOps,
  detailInitial, detailReduce, formatDateOnly, formatTotal,
} from "../../lib/productOrders.js";

/**
 * One order in full: its fields, its history, and the actions its status and
 * the viewer's role allow.
 *
 * EVERYTHING HERE IS BOUND TO THE SELECTED ORDER, THIS VISIT, THE LATEST
 * REQUEST. The state is a reducer (lib/productOrders.js: detailInitial /
 * detailReduce) and the sequence that drives it — select, the loads it
 * issues, a mutation, the refresh after it, a retry — is createDetailOps from
 * the same file, with the API injected; this component only wires those to
 * React and renders. Every event carries the order id and the selection
 * generation (A→B→A is three generations), loads also a request number, and
 * the reducer ignores anything about another order, an earlier visit or an
 * obsolete request. So switching orders drops the previous detail and dialog
 * at once; a reply that arrives late for an earlier visit, a mutation that
 * settles after the user moved on, or the older of two overlapping reloads
 * changes nothing.
 *
 * ONE RECOVERY PATH. ops.refresh() is the only way a detail reaches the
 * screen. After a mutation whose outcome is unknown the detail is locked;
 * after a saved or conflicting one it is stale. Both disable every action,
 * and only a validated `loaded` for this order from the latest request
 * re-enables them — the PageError retry, the "Reload order" link and the
 * refresh after every mutation all call ops.refresh(). A conflict closes the
 * dialog; once the fresh detail is shown a new, deliberate action is needed.
 *
 * What happens after a change is sent:
 *   verified success   stale until refresh() shows the server's state
 *   400 / 403 / 404    the server refused before writing: message and field
 *                      errors beside the action, the dialog stays open
 *   409                "This order changed." stale until refresh(); then a
 *                      fresh action is needed
 *   anything else      UNKNOWN. The action is NOT retried. Locked until
 *                      refresh() succeeds; a failed refresh keeps the lock
 *                      and offers the reload again
 * A locally invalid edit never becomes a request: its field errors are shown
 * on what was typed (validationFailed), and the edit stays open.
 * A 401 is handled where every other page handles it: useApiAction logs out.
 *
 * mode "billing" (Delma's tab): the actions are billingActions — an
 * accountant records invoices and nothing else; an admin also has revise and
 * correction — and the billing description is shown for ready and invoiced
 * orders with a Copy button that falls back to selecting the text when the
 * clipboard is unavailable or refuses.
 */
const EVENT_LABELS = {
  created: "Order recorded", edit: "Request edited", "prepare-billing": "Approval recorded — ready for billing", revise: "Returned for a revised quote",
  invoiced: "Invoice recorded", "correct-invoice": "Invoice recording corrected", cancel: "Cancelled",
};
const fmtWhen = (iso) => new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });

export function ProductOrderDetail({ id, onClose, onChanged, mode = "admin" }) {
  const { user } = useAuth();
  const api = useApiAction();
  const [state, dispatch] = useReducer(detailReduce, null, detailInitial);
  // The reduced state, readable by ops after an await.
  const stateRef = useRef(state);
  stateRef.current = state;
  const onChangedRef = useRef(onChanged);
  onChangedRef.current = onChanged;
  const ops = useMemo(() => createDetailOps({ api, dispatch, getState: () => stateRef.current, onChanged: () => onChangedRef.current?.() }), [api]);

  useEffect(() => { ops.select(id); }, [id, ops]);

  const { order } = state;
  const canAct = canActOnDetail(state);
  const blocked = state.locked || state.stale;
  // "copied" | "failed" | null — the Copy button's last outcome, for this order only.
  const [copied, setCopied] = useState(null);
  const descriptionRef = useRef(null);
  useEffect(() => { setCopied(null); }, [id]);
  async function copyDescription() {
    const ok = await copyText(billingDescription(order), typeof navigator === "undefined" ? undefined : navigator.clipboard);
    setCopied(ok ? "copied" : "failed");
    if (!ok && descriptionRef.current && typeof window !== "undefined" && window.getSelection) {
      const range = document.createRange(); range.selectNodeContents(descriptionRef.current);
      const sel = window.getSelection(); sel.removeAllRanges(); sel.addRange(range);
    }
  }

  if (state.loading && !order) return <PageLoading label="Loading order…" />;
  if (!order) return <PageError error={state.error ?? { message: "The order could not be loaded." }} onRetry={() => ops.refresh()} />;

  const actions = mode === "billing" ? billingActions(order, user?.role) : availableActions(order, user?.role);
  const { notice } = state;

  return (
    <Card>
      <div className="px-5 py-4 border-b border-border-light flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[13px] font-semibold text-text-primary leading-tight">{order.client.name} · {PRODUCT_TYPE_LABELS[order.productType]}</div>
          <div className="text-[11px] text-text-muted mt-0.5">Order {order.id} · version {order.version}</div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <StatusChip status={order.status} />
          {onClose && <button type="button" onClick={onClose} className="text-[12px] text-text-muted hover:text-text-primary" aria-label="Close order">✕</button>}
        </div>
      </div>

      {notice && (
        <div role={notice.tone === "info" ? "status" : "alert"} className={`px-5 py-2.5 text-[12px] border-b border-border-light ${notice.tone === "error" ? "text-negative" : notice.tone === "warning" ? "text-[#8a4b1a] bg-[#fbeae0]" : "text-positive"}`}>
          {notice.text}
          {blocked && <button type="button" onClick={() => ops.refresh()} className="ml-2 underline">Reload order</button>}
        </div>
      )}
      {state.error && (
        <div role="alert" className="px-5 py-2.5 text-[12px] text-negative border-b border-border-light">
          The latest reload failed: {state.error.message}. The details shown may be out of date.
          <button type="button" onClick={() => ops.refresh()} className="ml-2 underline">Try again</button>
        </div>
      )}

      {state.action === "edit" ? (
        <div className="px-5 py-4">
          <div className="text-[12px] font-medium text-text-primary mb-2">Edit request · version {order.version}</div>
          <ProductOrderForm mode="edit" initial={order} errors={state.fieldErrors} pending={state.busy} disabled={!canAct} submitLabel="Save changes" onCancel={ops.closeAction}
            onSubmit={ops.submitEdit} />
        </div>
      ) : (
        <div className="px-5 py-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-[13px]">
          <div className="sm:col-span-2">
            <div className="text-[10px] uppercase text-text-muted font-medium" style={{ letterSpacing: "0.5px" }}>Description</div>
            <div className="whitespace-pre-wrap break-words text-text-primary">{order.description}</div>
          </div>
          <Item label="Quantity" value={order.quantity.toLocaleString()} />
          <Item label="Requested delivery" value={formatDateOnly(order.requestedDeliveryDate)} muted={order.requestedDeliveryDate === null} />
          <Item label="Approved total" value={formatTotal(order.approvedTotal)} muted={order.approvedTotal === null} />
          {order.approvedTotal !== null && <Item label="Approved by" value={`${order.approvedByName} · ${formatDateOnly(order.approvalDate)}`} />}
          {order.approvedTotal !== null && <div className="sm:col-span-2"><Item label="Approval reference" value={order.approvalReference} /></div>}
          {order.qbInvoiceNumber !== null && <Item label="QuickBooks invoice" value={`${order.qbInvoiceNumber} · ${formatDateOnly(order.invoiceDate)}`} />}
          {order.cancellationReason !== null && <div className="sm:col-span-2"><Item label="Cancelled" value={order.cancellationReason} /></div>}
          {(order.status === "ready_for_billing" || (mode === "billing" && order.status === "invoiced")) && (
            <div className="sm:col-span-2">
              <div className="flex items-center justify-between gap-2">
                <div className="text-[10px] uppercase text-text-muted font-medium" style={{ letterSpacing: "0.5px" }}>Billing description</div>
                <button type="button" onClick={copyDescription} className="text-[12px] text-accent hover:underline">Copy billing description</button>
              </div>
              <pre ref={descriptionRef} className="whitespace-pre-wrap break-words font-sans text-[12px] text-text-secondary bg-app-bg rounded p-2 mt-1 select-all">{billingDescription(order)}</pre>
              {copied === "copied" && <div role="status" className="text-[11px] text-positive mt-1">Copied.</div>}
              {copied === "failed" && <div role="status" className="text-[11px] text-[#8a4b1a] mt-1">Copying isn't available in this browser — the text above is selected; press Ctrl+C (⌘C) to copy it.</div>}
            </div>
          )}
        </div>
      )}

      {actions.length > 0 && state.action !== "edit" && (
        <div className="px-5 py-3 border-t border-border-light flex flex-wrap gap-2">
          {actions.map((a) => (
            <button key={a} type="button" disabled={!canAct} aria-disabled={!canAct || undefined} onClick={() => ops.openAction(a)}
              className={`px-3 py-1.5 rounded-card text-[12px] font-medium border disabled:opacity-50 ${a === "cancel" ? "border-border-default text-text-secondary" : "bg-accent text-white border-accent"}`}>
              {ACTION_LABELS[a]}
            </button>
          ))}
        </div>
      )}

      <div className="px-5 py-4 border-t border-border-light">
        <div className="text-[10px] uppercase text-text-muted font-medium mb-2" style={{ letterSpacing: "0.5px" }}>History</div>
        <ol className="space-y-2 text-[12px]">
          {order.events.map((e) => (
            <li key={e.id} className="flex gap-3">
              <span className="text-text-muted tabular-nums w-5 flex-shrink-0">v{e.version}</span>
              <div className="min-w-0">
                <div className="text-text-primary">{EVENT_LABELS[e.action]}{e.after?.status && e.action !== "created" ? <span className="text-text-muted"> → {e.after.status.replaceAll("_", " ")}</span> : null}</div>
                <div className="text-text-muted">{fmtWhen(e.createdAt)} · by {e.actorId}</div>
                {e.reason && <div className="text-text-secondary whitespace-pre-wrap break-words">Reason: {e.reason}</div>}
              </div>
            </li>
          ))}
        </ol>
      </div>

      {state.action && state.action !== "edit" && (
        <ProductOrderActionModal action={state.action} order={order} pending={state.busy} error={notice?.tone === "error" ? notice.text : null} serverErrors={state.fieldErrors}
          onClose={ops.closeAction} onSubmit={(input) => ops.change(state.action, input)} />
      )}
    </Card>
  );
}

function Item({ label, value, muted = false }) {
  return (
    <div>
      <div className="text-[10px] uppercase text-text-muted font-medium" style={{ letterSpacing: "0.5px" }}>{label}</div>
      <div className={`break-words ${muted ? "text-text-muted italic" : "text-text-primary"}`}>{value}</div>
    </div>
  );
}
